import React, { useEffect, useState, useRef } from "react";
import { Canvas, useLoader } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { TextureLoader } from "three/src/loaders/TextureLoader";
import * as THREE from "three";

// Import des images de fond
import fond1 from "./fond0.png";

// Composant pour le fond avec texture
function Background() {
  const texture = useLoader(TextureLoader, fond1);
  
  return (
    <mesh position={[0, 0, -5]}>
      <planeGeometry args={[10, 10]} />
      <meshBasicMaterial map={texture} side={THREE.FrontSide} />
    </mesh>
  );
}

function Model({ url, format }) {
  const group = useRef();

  useEffect(() => {
    if (!url) return;

    let loader;

    if (format === "obj") {
      loader = new OBJLoader();
    } else {
      loader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("https://www.gstatic.com/draco/v1/decoders/");
      loader.setDRACOLoader(dracoLoader);
    }

    loader.load(
      url,
      (loaded) => {
        if (group.current) {
          while (group.current.children.length) {
            group.current.remove(group.current.children[0]);
          }
          const object = format === "obj" ? loaded : loaded.scene;
          
          // Centrer le modèle
          const box = new THREE.Box3().setFromObject(object);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          
          // Calculer la distance optimale pour la caméra
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = maxDim > 0 ? 2 / maxDim : 1;
          
          // Centrer le modèle
          object.position.sub(center);
          
          // Ajuster l'échelle si nécessaire
          if (scale !== 1) {
            object.scale.setScalar(scale);
          }
          
          group.current.add(object);
        }
      },
      undefined,
      (error) => {
        console.error("Erreur chargement modèle 3D:", error);
      }
    );

    return () => {
      if (group.current) {
        while (group.current.children.length) {
          group.current.remove(group.current.children[0]);
        }
      }
    };
  }, [url, format]);

  return <group ref={group} />;
}

export default function ThreeModelViewer({
  base64,
  url,
  width = 300,
  height = 300,
}) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [format, setFormat] = useState(null);

  useEffect(() => {
    if (url) {
      let cleanUrl = url.trim();

      // Si l'URL commence par localhost + /uploads/ + une URL externe, on extrait l'URL externe
      const localUploadsPrefix = "http://localhost:5000/uploads/";
      if (cleanUrl.startsWith(localUploadsPrefix)) {
        // On extrait la partie après /uploads/
        const possibleExternalUrl = cleanUrl.slice(localUploadsPrefix.length);

        // Si la partie après localhost est une URL externe valide, on la garde
        if (
          possibleExternalUrl.startsWith("http://") ||
          possibleExternalUrl.startsWith("https://")
        ) {
          cleanUrl = possibleExternalUrl;
        }
      }

      // Correction d'URLs mal formées (ex: https:/...)
      if (!cleanUrl.match(/^https?:\/\//)) {
        cleanUrl = cleanUrl.replace(/^https?:\/(?!\/)/, (match) => match + "/");
      }

      try {
        const urlObj = new URL(cleanUrl);

        if (urlObj.hostname === window.location.hostname) {
          urlObj.pathname = urlObj.pathname.replace(
            /\/public\/uploads\//,
            "/uploads/"
          );
          urlObj.pathname = urlObj.pathname.replace(
            /(\/uploads)+\//g,
            "/uploads/"
          );
          urlObj.pathname = urlObj.pathname.replace(/\/+/g, "/");
          cleanUrl = urlObj.href;
        } else {
          cleanUrl = urlObj.href;
        }
      } catch (e) {
        console.warn("URL invalide, utilisation brute:", url);
        cleanUrl = url;
      }

      if (cleanUrl.match(/\.(obj)$/i)) {
        setFormat("obj");
      } else if (cleanUrl.match(/\.(gltf|glb)$/i)) {
        setFormat("gltf");
      } else {
        setFormat("gltf");
      }

      setObjectUrl(cleanUrl);
      return;
    }

    if (!base64) {
      setObjectUrl(null);
      setFormat(null);
      return;
    }

    try {
      const base64Data = base64.split(",")[1];
      if (!base64Data) throw new Error("Base64 mal formée");

      const mimeTypeMatch = base64.match(/data:([^;]+);base64,/);
      const mimeType = mimeTypeMatch ? mimeTypeMatch[1] : "";

      let ext = "obj";
      if (
        mimeType.includes("gltf") ||
        mimeType.includes("glb") ||
        mimeType.includes("gltf-binary")
      ) {
        ext = "gltf";
      } else if (mimeType.includes("obj")) {
        ext = "obj";
      }

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: mimeType });
      const createdUrl = URL.createObjectURL(blob);

      setObjectUrl(createdUrl);
      setFormat(ext);

      return () => {
        URL.revokeObjectURL(createdUrl);
        setObjectUrl(null);
        setFormat(null);
      };
    } catch (err) {
      console.error("Erreur lors du décodage du modèle 3D :", err);
      setObjectUrl(null);
      setFormat(null);
    }
  }, [base64, url]);

  if (!objectUrl)
    return <p style={{ textAlign: "center" }}>Chargement 3D...</p>;

  return (
    <div
      style={{
        width,
        height,
        borderRadius: 8,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Canvas
        camera={{ 
          position: [0, 0, 3],
          fov: 50,
          near: 0.1,
          far: 1000
        }}
        style={{ 
          width: "100%", 
          height: "100%",
          position: "relative",
        }}
        gl={{ 
          alpha: true,
          antialias: true 
        }}
      >
        {/* Background color */}
        <color attach="background" args={["#ebe9e5"]} />
        
        {/* Background avec texture */}
        <Background />
        
        {/* Éclairage amélioré */}
        <ambientLight intensity={0.8} color="#f4e4bc" />
        <directionalLight 
          position={[5, 5, 5]} 
          intensity={1.2} 
          color="#ffffff"
          castShadow
        />
        <directionalLight 
          position={[-5, -5, -5]} 
          intensity={0.3} 
          color="#d4af37"
        />
        
        <Model url={objectUrl} format={format} />
        <OrbitControls 
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={1}
          maxDistance={10}
          autoRotate={false}
          autoRotateSpeed={0.5}
        />
      </Canvas>
    </div>
  );
}
